import { globalResizeObserver } from 'base/ui/measure';
import { useStableCallback } from 'base/useStableCallback';
import { CSSProperties, useEffect, useMemo, useState } from 'react';

/**
 * Scales an element to fit its container.
 *
 * Use sparingly, as it takes a frame to measure the element and apply the
 * scaling. This can cause a flash of zero opacity content.
 *
 * @param opts.minScale - Sets the minimum scale that will be apply
 *        to the element. This is useful when dealing with very small scales as
 *        they can cause performance issues in Mobile Safari.
 *
 * @return {elementRef} A ref to the element to be scaled.
 * @return {elementStyle} The style to apply to the element.
 */
export function useScaledElement({
  minScale = 0,
}: { minScale?: number } = {}): {
  elementRef: (element: HTMLDivElement | null) => void;
  elementStyle: CSSProperties;
} {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [scale, setScale] = useState<number | null>(null);

  const handleResize = useStableCallback(() => {
    if (element?.parentElement == null) {
      return setScale(1);
    }

    const {
      width: elementWidth,
      paddingLeft: elementPaddingLeft,
      paddingRight: elementPaddingRight,
    } = getComputedStyle(element);
    const {
      width: containerWidth,
      paddingLeft: containerPaddingLeft,
      paddingRight: containerPaddingRight,
    } = getComputedStyle(element.parentElement);

    const totalElementWidth =
      parseFloat(elementWidth) -
      parseFloat(elementPaddingLeft) -
      parseFloat(elementPaddingRight);
    const totalContainerWidth =
      parseFloat(containerWidth) -
      parseFloat(containerPaddingLeft) -
      parseFloat(containerPaddingRight);

    setScale(Math.max(totalContainerWidth / totalElementWidth, minScale));
  });

  useEffect(
    () =>
      element?.parentElement
        ? globalResizeObserver.observe(element?.parentElement, handleResize)
        : undefined,
    [handleResize, element?.parentElement],
  );

  useEffect(
    () =>
      element ? globalResizeObserver.observe(element, handleResize) : undefined,
    [handleResize, element],
  );

  const elementStyle = useMemo(
    () =>
      scale == null
        ? {
            opacity: 0,
            width: 'fit-content',
          }
        : {
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            width: 'fit-content',
          },
    [scale],
  );

  return { elementRef: setElement, elementStyle };
}
